import { useImperativeHandle, useRef, useState, type Ref } from 'react';
import { campaignCopy, isStaleRowError, useCampaignCapabilitiesStore, useCampaignUiStore } from '@discovr/features';
import type { RollbackReasonCode, RosterEntryFinalized } from '@discovr/types';
import { useMenuDialogFocus } from '../hooks/useMenuDialogFocus';
import { mainPost } from './ConfirmedTab.helpers';
import { isPostHeld, rollbackTarget, type RollbackTarget } from './ConfirmedTab.hold';
import { RollbackApprovalDialog } from './RollbackApprovalDialog';

export interface RollbackDialogHandle {
  openRollback: (row: RosterEntryFinalized, opener?: HTMLElement | null) => void;
}

interface Subject {
  entryId: string;
  postId: string;
  target: RollbackTarget;
  creatorName: string;
}

export function ConfirmedRollbackDialog({ ref }: { ref: Ref<RollbackDialogHandle> }) {
  const { rollbackApproval, rollbackAcceptance } = useCampaignCapabilitiesStore();
  const campaignPipeline = useCampaignUiStore((s) => s.campaignPipeline);
  const [subject, setSubject] = useState<Subject | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const { rememberFocus, restoreFocus } = useMenuDialogFocus();

  useImperativeHandle(
    ref,
    () => ({
      openRollback: (row, opener) => {
        const post = mainPost(row);
        const target = rollbackTarget(post, campaignPipeline);
        if (!post || !target || isPostHeld(post)) return;
        if (!(target.kind === 'acceptance' ? rollbackAcceptance : rollbackApproval)) return;
        rememberFocus(opener);
        setSubject({
          entryId: row.id,
          postId: post.id,
          target,
          creatorName: row.creator?.name ?? campaignCopy.creator.hold.dialog.fallbackName,
        });
      },
    }),
    [rollbackApproval, rollbackAcceptance, campaignPipeline, rememberFocus],
  );

  const confirm = async (reasonCode: RollbackReasonCode, note: string) => {
    const submitted = subject;
    if (!submitted || inFlight.current) return;
    const { target } = submitted;
    const args = { entryId: submitted.entryId, postId: submitted.postId, reasonCode, ...(note ? { note } : {}) };
    const send =
      target.kind === 'acceptance'
        ? rollbackAcceptance && (() => rollbackAcceptance({ ...args, fromStatus: target.from }))
        : rollbackApproval && (() => rollbackApproval({ ...args, fromStatus: target.from }));
    if (!send) return;
    inFlight.current = true;
    setBusy(true);
    const close = () => setSubject((current) => (current === submitted ? null : current));
    try {
      await send();
      close();
    } catch (err) {
      // Toasted by the mutation hook. A stale row has been refetched, so a retry on
      // this captured subject would fail again; otherwise keep the input to retry.
      if (isStaleRowError(err)) close();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <RollbackApprovalDialog
      open={subject !== null}
      onOpenChange={(open) => !open && setSubject(null)}
      creatorName={subject?.creatorName ?? ''}
      kind={subject?.target.kind ?? null}
      busy={busy}
      onConfirm={confirm}
      onCloseAutoFocus={restoreFocus}
    />
  );
}
